/**
 * @fileoverview Admin Order Store (Zustand)
 * 
 * Manages admin order state: pending count, orders list, FCM tokens, notifications.
 * Replaces AdminOrderContext state with better performance.
 * 
 * @module stores/useAdminOrderStore
 */

import { create } from 'zustand';
import { getOrder, getOrderById } from '@/api';
import { coalesceByKey } from '@/utils/coalesce';
import { wsService } from '@/services/websocket';
import { autoPrintLocationTickets } from '@/context/printReceipt';
import { usePrintJobStore } from '@/stores/usePrintJobStore';
// Import swrMutate directly from its source module, NOT the '@/hooks/swr' barrel.
// The barrel re-exports ./useOrders, which imports this store — going through the
// barrel here closes a cross-chunk circular dependency (index.js ⇄ useOrders.js)
// that Rollup splits into different chunks, producing "broken execution order".
// That made React.lazy read `.default` off an undefined chunk namespace and crash
// lazy routes like /admin/tables. Importing the leaf module keeps the barrel out
// of the cycle. swrMutate is a direct named export of ./useOrders.
import { swrMutate } from '@/hooks/swr/useOrders';
import { useClickCollectStore } from './useClickCollectStore';

/**
 * Calculate pending orders count
 */
const calculatePendingCount = (ordersList) => {
  const filteredOrdersNotPaid = ordersList.filter((order) => order.status === 0);
  return filteredOrdersNotPaid.filter((order) =>
    order.order_menus.some((menu) => menu.status === 0)
  ).length;
};

/**
 * Admin Order Store
 * 
 * @example
 * const pendingCount = useAdminOrderStore((state) => state.pendingCount);
 * const fetchOrders = useAdminOrderStore((state) => state.fetchOrders);
 * 
 * // Or use the legacy hook for backward compatibility
 * const { pendingCount, orders, fetchOrders } = useAdminOrder();
 */

// One read of an order per burst of realtime events about it (see utils/coalesce.js).
export const ORDER_EVENT_COALESCE_MS = 250;
const refreshOrderSoon = coalesceByKey(async (router, orderId) => {
  try {
    // Fetch only the updated order by ID
    const updatedOrder = await getOrderById(router, orderId);
    if (updatedOrder) {
      // Update the single order in SWR cache (no full refetch!)
      await swrMutate.updateOrder(updatedOrder);
    } else {
      console.warn("Order not found:", orderId);
    }
  } catch (err) {
    console.error("Error handling order update:", err);
    // Fallback to full refetch if single order fetch fails
    await swrMutate.orders();
  }
}, ORDER_EVENT_COALESCE_MS);

export const useAdminOrderStore = create((set, get) => ({
  // State
  pendingCount: 0,
  orders: [],
  fcmToken: null,
  notificationPermission: 'default',
  tokenRegistered: false,
  userInteracted: false,

  // Internal state
  _initialized: false,
  _wsCleanup: null,
  _router: null,

  // Setters
  setPendingCount: (pendingCount) => set({ pendingCount }),
  setOrders: (orders) => set({ orders }),
  setFcmToken: (fcmToken) => set({ fcmToken }),
  setNotificationPermission: (notificationPermission) => set({ notificationPermission }),
  setTokenRegistered: (tokenRegistered) => set({ tokenRegistered }),
  setUserInteracted: (userInteracted) => set({ userInteracted }),
  setRouter: (router) => set({ _router: router }),

  // Fetch orders
  fetchOrders: async (router) => {
    const routerToUse = router || get()._router;
    if (!routerToUse) {
      console.warn('No router available for fetchOrders');
      return;
    }

    try {
      const response = await getOrder(routerToUse);
      if (response?.data) {
        const newPendingCount = calculatePendingCount(response.data);
        set({ 
          orders: response.data,
          pendingCount: newPendingCount
        });
        return response.data;
      } else {
        console.warn("No data received from getOrder.");
        return [];
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      return [];
    }
  },

  // Handle new order from WebSocket
  handleNewOrder: async (data, router, restaurant) => {
    const routerToUse = router || get()._router;

    // Handle click & collect orders separately
    if (data.message === "New order click & collect placed") {
      // Forward to Click & Collect store
      useClickCollectStore.getState().handleNewOrder(data, routerToUse);
      return;
    }

    if (data.orderId) {
      try {
        // Fetch only the new order by ID
        const newOrder = await getOrderById(routerToUse, data.orderId);

        if (newOrder) {
          // Add/update the single order in the SWR cache (addOrder also revalidates)
          await swrMutate.addOrder(newOrder);

          // Get the restaurant data from localStorage
          const restaurantData = JSON.parse(localStorage.getItem('restaurant_data') || '{}');
          const isAutoPrintEnabled = restaurantData.automatic_print === true;


          // If automatic printing is enabled, print the order
          if (isAutoPrintEnabled && routerToUse) {

            const restaurantInfo = {
              ...restaurant,
              automatic_print: restaurantData.automatic_print,
              name: restaurant?.name || restaurantData.name
            };

            await autoPrintLocationTickets(routerToUse, newOrder, restaurantInfo);
          }
        } else {
          console.warn("Order not found:", data.orderId);
        }
      } catch (err) {
        console.error("Error handling new order:", err);
        // Fallback to full refetch if single order fetch fails
        await swrMutate.orders();
      }
    } else {
      // No orderId provided, fallback to full refetch
      await swrMutate.orders();
    }
  },

  // Track recently updated orders locally (to avoid double fetch)
  _recentLocalUpdates: new Set(),

  // Mark an order as recently updated locally
  markLocalUpdate: (orderId) => {
    const recentUpdates = get()._recentLocalUpdates;
    recentUpdates.add(orderId);
    // Clear after 2 seconds
    setTimeout(() => {
      recentUpdates.delete(orderId);
    }, 2000);
  },

  // Handle order update from WebSocket
  handleOrderUpdate: async (data, router) => {
    const routerToUse = router || get()._router;
    // The API sends `orderId` or, from some endpoints, `order_id`.
    const orderId = data?.orderId ?? data?.order_id;

    if (orderId) {
      // Skip if this device just updated this order (avoid double fetch)
      const recentUpdates = get()._recentLocalUpdates;
      if (recentUpdates.has(orderId)) {
        return;
      }

      // A payment sends order::update several times (+ order::refunded…): one read of the
      // order after the burst, not one per event (QA 19/10 round 2 — order/80803 read ×3).
      return refreshOrderSoon(String(orderId), routerToUse, orderId);
    }
  },

  // Initialize WebSocket connection
  initializeWebSocket: (router, restaurant) => {
    const user = JSON.parse(localStorage.getItem("userObj") || "{}");
    const restaurant_id = restaurant?.id ?? user?.restaurant_id;

    if (!restaurant_id) {
      console.warn("No restaurant ID found for WebSocket");
      return;
    }

    // Clean up the previous subscription (and leave the previous restaurant's room)
    const existingCleanup = get()._wsCleanup;
    if (existingCleanup) {
      existingCleanup();
    }

    const room = `admin-restaurant:${restaurant_id}`;

    const handleNewOrder = (data) => {
      get().handleNewOrder(data, router, restaurant);
    };

    const handleOrderUpdate = (data) => {
      get().handleOrderUpdate(data, router);
    };

    // Reconnected without a complete replay: some events may be lost, refetch.
    const release = wsService.join(room, { onResync: () => { swrMutate.orders(); } });
    // Print queue: real status of every ticket (queued / sent / printed / failed).
    const printJobs = usePrintJobStore.getState();
    printJobs.reset();
    printJobs.loadCapabilities(router, { force: true });
    printJobs.loadRecent(router);
    const handlePrintJob = (data) => {
      usePrintJobStore.getState().upsert(data);
    };

    const offs = [
      wsService.on('order::new', handleNewOrder, { room }),
      wsService.on('order::update', handleOrderUpdate, { room }),
      // A refund changes the order's status/amounts: refresh that order.
      wsService.on('order::refunded', handleOrderUpdate, { room }),
      wsService.on('print::job', handlePrintJob, { room }),
    ];

    set({
      _wsCleanup: () => {
        offs.forEach((off) => off());
        release();
      },
      _router: router
    });
  },

  // Initialize store
  initialize: (router, restaurant) => {
    if (get()._initialized) return;

    // Track user interaction
    const handleInteraction = () => set({ userInteracted: true });
    document.addEventListener('click', handleInteraction);

    // Initialize WebSocket
    if (restaurant?.id) {
      get().initializeWebSocket(router, restaurant);
    }

    set({
      _initialized: true,
      _router: router
    });

    return () => {
      document.removeEventListener('click', handleInteraction);
      const wsCleanup = get()._wsCleanup;
      if (wsCleanup) wsCleanup();
    };
  },

  // Cleanup
  cleanup: () => {
    const wsCleanup = get()._wsCleanup;
    if (wsCleanup) wsCleanup();
    set({ 
      _initialized: false, 
      _wsCleanup: null,
      _router: null
    });
  },

  // Reset store
  reset: () => {
    get().cleanup();
    set({
      pendingCount: 0,
      orders: [],
      fcmToken: null,
      notificationPermission: 'default',
      tokenRegistered: false,
      userInteracted: false,
      _initialized: false,
      _wsCleanup: null,
      _router: null
    });
  }
}));

/**
 * Legacy hook for backward compatibility with hybrid SWR + Zustand approach
 * Uses SWR for orders data, Zustand for pendingCount (synced from SWR) and UI state
 * 
 * @example
 * const { pendingCount, orders, fetchOrders } = useAdminOrder();
 */
export const useAdminOrder = () => {
  const store = useAdminOrderStore();

  return {
    // Zustand state (kept for convenience and backward compatibility)
    pendingCount: store.pendingCount,
    setPendingCount: store.setPendingCount,
    orders: store.orders,
    setOrders: store.setOrders,
    fetchOrders: store.fetchOrders,

    // UI state from Zustand
    notificationPermission: store.notificationPermission,
    fcmToken: store.fcmToken,
    tokenRegistered: store.tokenRegistered,
    userInteracted: store.userInteracted,

    // Notification handler
    requestNotificationPermission: store._requestNotificationPermission || (() => Promise.resolve()),
    restaurantId: null, // Will be available from useRole
  };
};